import { buildVideoElement } from '../ProductCardVideo';

/**
 * Generates an IntersectionObserver callback for lazy-loading images.
 * Loads images when their anchor wrappers become visible in the viewport.
 *
 * @param {boolean} [trackFalsePositive=false] - Whether to track false positives for zoom gallery. It's optional
 * @returns {Function} IntersectionObserver callback.
 */
export const generateMediaObserverCallback = (trackFalsePositive = false) => {
  return (entries, observer) => {
    entries.forEach((entry) => {
      // Track a false positve on zoom gallery
      if (trackFalsePositive) {
        if (entry.isIntersecting && entry.intersectionRatio == 1 && entry.target.style.order !== '1') {
          return;
        }
      }

      if (entry.isIntersecting) {
        // Intersect image element
        const anchor = entry.target;
        // Disconnect observer if an anchor already contains an image
        if (!Object.keys(anchor.dataset).length) {
          return observer.disconnect();
        }

        // Build a second media element either image or video
        let videoElement;
        const secondPictureElement = document.createElement('picture');

        const secondMediaType = anchor.dataset.secondMediaType;
        if (secondMediaType === 'video') {
          videoElement = buildVideoElement(anchor.dataset.secondMediaSrc, anchor.dataset.secondMediaAlt, {
            placement: 'tile',
          });

          delete anchor.dataset['second-media-type'];
          delete anchor.dataset['second-media-src'];
        }
        // Build the second image element
        if (secondMediaType === 'image') {
          secondPictureElement.setAttribute('data-second-element', '');
          const secondImageElement = document.createElement('img');
          const secondMediaConditions = anchor.dataset.secondMediaSizes?.split(',');
          const secondSrcsetArray = anchor.dataset.secondMediaSrcset?.split('|');
          delete anchor.dataset['second-media-sizes'];
          delete anchor.dataset['second-media-srcset'];

          secondMediaConditions?.forEach((mediaCondition, index) => {
            const sourceElement = document.createElement('source');
            sourceElement.media = mediaCondition;
            sourceElement.srcset = secondSrcsetArray[index];
            // Append every source element in picture element
            secondPictureElement.appendChild(sourceElement);
          });

          // Append image element in picture element
          secondPictureElement.appendChild(secondImageElement);

          delete anchor.dataset['second-media-type'];

          secondImageElement.alt = anchor.dataset.secondMediaAlt;
          delete anchor.dataset['second-media-alt'];

          secondImageElement.loading = anchor.dataset.secondMediaLoading;
          delete anchor.dataset['second-media-loading'];

          secondImageElement.src = anchor.dataset.secondMediaSrc;
          delete anchor.dataset['second-media-src'];

          // Handle second image load errors
          secondImageElement.onerror = function () {
            this.parentNode.style.display = 'none';
          };
        }

        // Build the first picture element
        const pictureElement = document.createElement('picture');
        pictureElement.setAttribute('data-first-element', '');
        const imageElement = document.createElement('img');
        const mediaConditions = anchor.dataset.sizes?.split(',');
        const srcsetArray = anchor.dataset.srcset?.split('|');
        delete anchor.dataset['sizes'];
        delete anchor.dataset['srcset'];

        mediaConditions?.forEach((mediaCondition, index) => {
          const sourceElement = document.createElement('source');
          sourceElement.media = mediaCondition;
          sourceElement.srcset = srcsetArray[index];
          // Append every source element in picture element
          pictureElement.appendChild(sourceElement);
        });

        // Append image element in picture element
        pictureElement.appendChild(imageElement);

        // This scans all data-* attributes and set each image attribute. Then, removes each data-* attribute
        for (let key in anchor.dataset) {
          imageElement[key] = anchor.dataset[key];
          delete anchor.dataset[key];
        }

        // Appends image element into anchor and set an onload function
        imageElement.onload = () => observer.disconnect();

        // Handle image load errors (no network request fallback)
        imageElement.onerror = function () {
          this.parentNode.style.display = 'none';
          this.closest('a').style.background = '#fff';
        };

        // Inspect if there is a quick add button to keep it to front
        const quickAddButton = anchor.querySelector('.product-card-c-quick-add');

        // Append picture and second media elements into anchor based on other existing elements. For example, quick add button
        customAppendNode(anchor, pictureElement, quickAddButton);

        if (secondMediaType === 'video') {
          customAppendNode(anchor, videoElement, quickAddButton);
        } else if (secondMediaType === 'image') {
          customAppendNode(anchor, secondPictureElement, quickAddButton);
        }

        // Look for Color Swatches to load
        const colorSwatches = anchor.parentNode.nextSibling.querySelectorAll('a');

        colorSwatches.forEach((colorSwatch) => {
          colorSwatch.style.backgroundImage = colorSwatch.dataset.backgroundImage;
          delete colorSwatch.dataset['background-image'];
        });
        // Preload potential next and previous images in product recommendation gallery
        preloadImages(anchor);
      }
    });
  };
};

/**
 * Appends mainElement into anchor either before stepElement or at the end.
 * @param {HTMLAnchorElement} anchor
 * @param {HTMLPictureElement || HTMLVideoElement} mainElement
 * @param {HTMLUListElement} stepElement
 * @returns {Node} inserted node
 */
const customAppendNode = (anchor, mainElement, stepElement) => {
  if (stepElement) {
    return anchor.insertBefore(mainElement, stepElement);
  }
  return anchor.appendChild(mainElement);
};

/**
 * Preloads images for the product recommendation gallery.
 * @param {HTMLDivElemente | HTMLAnchorElement} anchor - the anchor container element
 */
const preloadImages = (anchor) => {
  const nodes = Array.from(anchor.closest('.carousel-c-scroll__container').querySelectorAll('div[id*="carousel_"]'));
  const currentIndex = nodes.findIndex((node) => node.id === anchor.parentNode.id);
  let nextEl = null;

  // Check next and previous elements
  if (currentIndex + 1 < nodes.length) {
    nextEl = nodes[currentIndex + 1].querySelector('a');
  }

  // Build and link elements on the head for preloading them whenever they exist
  nextEl && Object.keys(nextEl.dataset).length && buildLink(nextEl);
};

/**
 * Extracts data information and builds a preload link element for a given image container.
 * By default, this will preload the first image element visible in image container.
 * This won't preload second media resource.
 * @param {HTMLDivElement} el - The anchor container element.
 */
const buildLink = (el) => {
  const link = document.createElement('link');
  const sizesParts = el.dataset.sizes.split(',');
  const srcSetParts = el.dataset.srcset.split('|');
  let sizes = sizesParts[2]; // we will choose sizes for screen bigger than 1800 by default
  let srcSet = srcSetParts[2];

  if (window.innerWidth < 990) {
    sizes = sizesParts[0];
    srcSet = srcSetParts[0];
  }
  if (window.innerWidth >= 990 && window.innerWidth < 1800) {
    sizes = sizesParts[1];
    srcSet = srcSetParts[1];
  }

  link.rel = 'preload';
  link.as = 'image';
  link.imageSizes = sizes;
  link.imageSrcset = srcSet;
  document.getElementsByTagName('head')[0].appendChild(link);
};

/**
 * Generates options for the IntersectionObserver used for images.
 *
 * @param {HTMLElement|null} rootElement - The root element for the observer.
 * @returns {Object} IntersectionObserver options.
 */
export const generateMediaObserverOptions = (rootElement) => {
  return {
    root: rootElement || null,
    rootMargin: '0px',
    threshold: 0.1, // Don't use 1.0 because it is unrealistic to meet on Safari browsers
  };
};
